import { useState, useMemo, useEffect } from 'react'
import { ArrowLeft, Trophy, TrendingUp, Calendar, MapPin, Clock, CheckCircle, Play, AlertCircle, X, RefreshCw, Share2, Loader2 } from 'lucide-react'
import { trackCategoryDetailActivity } from '../config/analytics'
import { generateMatchImage, shareMatchImage, shareServerMatchImage } from '../services/shareService'
import { functions } from '../config/firebase'
import { httpsCallable } from 'firebase/functions'

const ACTIVE_TAB_STORAGE_KEY = 'tournament-viewer-active-tab'

function CategoryView({ category, tournament, colorTheme, themeClasses, isDarkMode, exportedAt, onRefresh, onBack }) {
  // Initialize activeTab from localStorage if available
  const [activeTab, setActiveTab] = useState(() => {
    try {
      const saved = localStorage.getItem(ACTIVE_TAB_STORAGE_KEY)
      return saved === 'standings' ? 'standings' : 'matches'
    } catch {
      return 'matches'
    }
  })
  
  const [selectedTeam, setSelectedTeam] = useState(null)
  const [selectedStage, setSelectedStage] = useState(null)
  const [sharingMatchId, setSharingMatchId] = useState(null)
  const [shareToast, setShareToast] = useState(null)
  const [shareModalMatch, setShareModalMatch] = useState(null)

  const handleShareMatch = async (matchId) => {
    setSharingMatchId(matchId)
    try {
      const match = category.matches.find(m => m.id === matchId)
      if (!match) throw new Error('Match not found')
      const blob = await generateMatchImage({
        team1Name: match.team1Name || `${getPlayerName(match.team1Player1)} / ${getPlayerName(match.team1Player2)}`,
        team2Name: match.team2Name || `${getPlayerName(match.team2Player1)} / ${getPlayerName(match.team2Player2)}`,
        team1Score: match.team1Score,
        team2Score: match.team2Score,
        winnerId: match.winnerId,
        team1Id: match.team1Id,
        team2Id: match.team2Id,
        stageName: match.stageName || '',
        tournamentName: tournament?.name || 'Tournament',
      })
      const result = await shareMatchImage(blob, matchId)
      if (result === 'shared') {
        setShareToast({ type: 'shared', message: 'Shared!' })
      } else if (result === 'downloaded') {
        setShareToast({ type: 'copied', message: 'Image downloaded!' })
      }
    } catch (err) {
      console.error('Share error:', err)
      setShareToast({ type: 'error', message: `Share failed: ${err?.message || 'Unknown error'}` })
    } finally {
      setSharingMatchId(null)
      setTimeout(() => setShareToast(null), 3000)
    }
  }

  const handleShareServerImage = async (match) => {
    setSharingMatchId(match.id)
    try {
      const result = await shareServerMatchImage(match.matchCardUrl, match.id)
      if (result === 'shared') {
        setShareToast({ type: 'shared', message: 'Shared!' })
      } else if (result === 'downloaded') {
        setShareToast({ type: 'copied', message: 'Image downloaded!' })
      }
    } catch (err) {
      console.error('Share error:', err)
      setShareToast({ type: 'error', message: `Share failed: ${err?.message || 'Unknown error'}` })
    } finally {
      setSharingMatchId(null)
      setTimeout(() => setShareToast(null), 3000)
    }
  }

  const handleGenerateAndShare = async (match) => {
    setSharingMatchId(match.id)
    try {
      const generateMatchCard = httpsCallable(functions, 'generateMatchCard')
      const result = await generateMatchCard({ matchId: match.id })
      const shareResult = await shareServerMatchImage(result.data.url, match.id)
      if (shareResult === 'shared') {
        setShareToast({ type: 'shared', message: 'Shared!' })
      } else if (shareResult === 'downloaded') {
        setShareToast({ type: 'copied', message: 'Image downloaded!' })
      }
    } catch (err) {
      console.error('Generate and share error:', err)
      setShareToast({ type: 'error', message: `Share failed: ${err?.message || 'Unknown error'}` })
    } finally {
      setSharingMatchId(null)
      setTimeout(() => setShareToast(null), 3000)
    }
  }

  // Track category detail viewed
  useEffect(() => {
    trackCategoryDetailActivity('Viewed', { label: category.title, category_id: category.id })
  }, [category.id])
  
  // Save activeTab to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem(ACTIVE_TAB_STORAGE_KEY, activeTab)
    } catch (err) {
      console.error('Failed to save active tab:', err)
    }
  }, [activeTab])
  
  // Force re-render when exportedAt changes
  const [dataVersion, setDataVersion] = useState(exportedAt)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [refreshCounter, setRefreshCounter] = useState(0)
  
  // Manual refresh handler
  const handleManualRefresh = async () => {
    trackCategoryDetailActivity('Manual Refresh', { label: category.title, category_id: category.id })
    setIsRefreshing(true)

    // Call the refresh function from App to fetch fresh data
    if (onRefresh) {
      await onRefresh()
    }

    // Increment refresh counter to force complete re-render
    setRefreshCounter(prev => prev + 1)

    // Force data version update to trigger recalculation
    const newVersion = Date.now().toString()
    setDataVersion(newVersion)
    
    // Reset all local state to clear any cached data
    setSelectedTeam(null)
    setSelectedStage(null)
    setActiveTab('matches')

    // Stop refresh animation after 1 second
    setTimeout(() => setIsRefreshing(false), 1000)
  }
  
  // Reset component state when data changes
  useEffect(() => {
    if (exportedAt !== dataVersion) {
      setDataVersion(exportedAt)
      // Reset any local state that might cache old data
      setSelectedTeam(null)
      setSelectedStage(null)
    }
  }, [exportedAt]) // Only watch exportedAt, not dataVersion
  
  // Format the exported timestamp
  const formatExportedAt = (timestamp) => {
    if (!timestamp) return 'Unknown'
    try {
      const date = new Date(timestamp)
      return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      })
    } catch {
      return 'Unknown'
    }
  }

  // Helper function to safely render player names
  const getPlayerName = (player) => {
    if (!player) return 'Unknown'
    if (typeof player === 'string') return player
    if (typeof player === 'object' && player.firstName && player.lastName) {
      return `${player.firstName} ${player.lastName}`
    }
    if (typeof player === 'object' && player.firstName) {
      return player.firstName
    }
    return 'Unknown'
  }

  // Get unique stages from matches
  const stages = useMemo(() => {
    const stageMap = new Map()
    
    // First, create stage map from config if available to preserve order
    // Check category config first, then tournament config
    const stagesConfig = category.customConfig?.stages || tournament.customConfig?.stages
    
    if (stagesConfig) {
      stagesConfig.forEach(stageConfig => {
        stageMap.set(stageConfig.name, {
          name: stageConfig.name,
          matches: [],
          order: typeof stageConfig.order === 'number' ? stageConfig.order : 0
        })
      })
    }
    
    // Then add matches to stages
    category.matches.forEach(match => {
      const stageName = match.stageName || 'Main Stage'
      if (!stageMap.has(stageName)) {
        // For unconfigured stages, use a very low order so they appear last
        stageMap.set(stageName, {
          name: stageName,
          matches: [],
          order: -999
        })
      }
      stageMap.get(stageName).matches.push(match)
    })

    // Place "Battle for Third" right after Finals
    const finalsStage = Array.from(stageMap.values()).find(s => s.name.toLowerCase().includes('finals') && !s.name.toLowerCase().includes('semi'))
    const thirdPlaceStage = stageMap.get('Battle for Third')
    if (finalsStage && thirdPlaceStage && thirdPlaceStage.order === -999) {
      thirdPlaceStage.order = finalsStage.order - 0.5
    }
    
    // Convert to array and sort: started stages first (by order desc), then not-started stages (by order desc)
    const stagesArray = Array.from(stageMap.values())
      .filter(stage => stage.matches.length > 0) // Only include stages with matches
      .sort((a, b) => {
        const aRealMatches = a.matches.filter(m => m.team1Id && m.team2Id)
        const bRealMatches = b.matches.filter(m => m.team1Id && m.team2Id)
        const aStarted = aRealMatches.some(m => m.status === 'completed' || m.status === 'in_progress')
        const bStarted = bRealMatches.some(m => m.status === 'completed' || m.status === 'in_progress')
        // Started stages come first
        if (aStarted && !bStarted) return -1
        if (!aStarted && bStarted) return 1
        // Within same group, sort by order descending
        return b.order - a.order
      })
    
    return stagesArray
  }, [category, tournament, exportedAt, dataVersion, refreshCounter])

  // Check if a stage is complete
  const isStageComplete = (stage) => {
    return stage.matches.every(m => m.status === 'completed')
  }

  // Get advancing teams count - should be the next stage's numberOfTeams
  const getAdvancingTeamsCount = (stageName) => {
    const stagesConfig = category.customConfig?.stages || tournament.customConfig?.stages
    if (!stagesConfig) return null
    
    // Find current stage
    const currentStage = stagesConfig.find(s => s.name === stageName)
    if (!currentStage) return null
    
    // Find next stage (stage with order = currentStage.order + 1)
    const nextStage = stagesConfig.find(s => s.order === currentStage.order + 1)
    
    // If there's a next stage, use its numberOfTeams as the advancing count
    if (nextStage && nextStage.numberOfTeams) {
      return nextStage.numberOfTeams
    }
    
    // Fallback: use teamsAdvancingPerBracket * numberOfBrackets
    if (currentStage.teamsAdvancingPerBracket && currentStage.numberOfBrackets) {
      return currentStage.teamsAdvancingPerBracket * currentStage.numberOfBrackets
    }
    
    return null
  }

  // Get stage format (knockout or round_robin)
  const getStageFormat = (stageName) => {
    const stagesConfig = category.customConfig?.stages || tournament.customConfig?.stages
    if (!stagesConfig) return null
    const stageConfig = stagesConfig.find(s => s.name === stageName)
    return stageConfig?.format || null
  }

  // Calculate standings per stage
  const standingsByStage = useMemo(() => {
    return stages.map(stage => {
      const teamStats = new Map()
      const advancingCount = getAdvancingTeamsCount(stage.name)
      const stageFormat = getStageFormat(stage.name)

      // Initialize all teams that played in this stage
      stage.matches.forEach(match => {
        if (!teamStats.has(match.team1Id)) {
          teamStats.set(match.team1Id, {
            teamId: match.team1Id,
            teamName: match.team1Name,
            player1Name: match.team1Player1,
            player2Name: match.team1Player2,
            bracketNumber: match.bracketNumber,
            wins: 0,
            losses: 0,
            pointsFor: 0,
            pointsAgainst: 0,
            matchesPlayed: 0
          })
        }
        if (!teamStats.has(match.team2Id)) {
          teamStats.set(match.team2Id, {
            teamId: match.team2Id,
            teamName: match.team2Name,
            player1Name: match.team2Player1,
            player2Name: match.team2Player2,
            bracketNumber: match.bracketNumber,
            wins: 0,
            losses: 0,
            pointsFor: 0,
            pointsAgainst: 0,
            matchesPlayed: 0
          })
        }
      })

      // Process completed matches in this stage
      stage.matches
        .filter(m => m.status === 'completed')
        .forEach(match => {
          const team1Stats = teamStats.get(match.team1Id)
          const team2Stats = teamStats.get(match.team2Id)

          if (team1Stats && team2Stats) {
            team1Stats.matchesPlayed++
            team2Stats.matchesPlayed++
            team1Stats.pointsFor += match.team1Score || 0
            team1Stats.pointsAgainst += match.team2Score || 0
            team2Stats.pointsFor += match.team2Score || 0
            team2Stats.pointsAgainst += match.team1Score || 0

            if (match.winnerId === match.team1Id) {
              team1Stats.wins++
              team2Stats.losses++
            } else if (match.winnerId === match.team2Id) {
              team2Stats.wins++
              team1Stats.losses++
            }
          }
        })

      // Check if this is the final stage (no more stages after this)
      const stagesConfig = category.customConfig?.stages || tournament.customConfig?.stages
      const currentStageConfig = stagesConfig?.find(s => s.name === stage.name)
      const isFinalStage = stagesConfig ? !stagesConfig.some(s => s.order > (currentStageConfig?.order || 0)) : false
      
      // Only apply battle for third logic to the final stage
      let hasBattleForThird = false
      let finalsMatch = null
      let thirdPlaceMatch = null
      
      if (isFinalStage) {
        // Derive if this final stage has a battle for third place
        const completedMatches = stage.matches.filter(m => m.status === 'completed')

        if (completedMatches.length >= 2) {
          // Try to use isThirdPlaceMatch flag first
          thirdPlaceMatch = completedMatches.find(m => m.isThirdPlaceMatch === true)
          finalsMatch = completedMatches.find(m => m.isThirdPlaceMatch !== true)

          // Fallback: if flag not set, identify by checking previous round losers
          if (!thirdPlaceMatch || !finalsMatch) {
            const allMatches = stages.flatMap(s => s.matches)
            const previousRound = stage.order - 1
            const previousRoundMatches = allMatches.filter(m =>
              m.round === previousRound &&
              m.status === 'completed'
            )
            const previousLosers = previousRoundMatches.map(m => {
              const loserId = m.team1Id === m.winnerId ? m.team2Id : m.team1Id
              return { id: loserId }
            })
            thirdPlaceMatch = completedMatches.find(m => {
              const team1IsLoser = previousLosers.some(l => l.id === m.team1Id)
              const team2IsLoser = previousLosers.some(l => l.id === m.team2Id)
              return team1IsLoser && team2IsLoser
            })
            finalsMatch = completedMatches.find(m => m.id !== thirdPlaceMatch?.id)
          }

          hasBattleForThird = !!thirdPlaceMatch
        } else if (completedMatches.length === 1) {
          // Only one match - must be the finals
          finalsMatch = completedMatches[0]
          hasBattleForThird = false
        }
        
      }
      
      // Convert to array and sort
      let standings = Array.from(teamStats.values())
      
      if (hasBattleForThird && finalsMatch && thirdPlaceMatch) {
        // Special ranking for finals with battle for third
        // Determine the loser of the finals match
        const finalsLoserId = finalsMatch.team1Id === finalsMatch.winnerId 
          ? finalsMatch.team2Id 
          : finalsMatch.team1Id
        
        // Determine the loser of the third place match
        const thirdPlaceLoserId = thirdPlaceMatch.team1Id === thirdPlaceMatch.winnerId
          ? thirdPlaceMatch.team2Id
          : thirdPlaceMatch.team1Id
        
        standings = standings.map(team => {
          if (team.teamId === finalsMatch.winnerId) {
            return { ...team, rank: 1 }
          } else if (team.teamId === finalsLoserId) {
            return { ...team, rank: 2 } // Loser of finals
          } else if (team.teamId === thirdPlaceMatch.winnerId) {
            return { ...team, rank: 3 }
          } else if (team.teamId === thirdPlaceLoserId) {
            return { ...team, rank: 4 }
          } else {
            // Fallback: shouldn't happen but just in case
            return { ...team, rank: 5 }
          }
        }).sort((a, b) => a.rank - b.rank)
      } else {
        // Normal sorting by wins and point differential (for all other stages)
        standings.sort((a, b) => {
          if (b.wins !== a.wins) return b.wins - a.wins
          const aDiff = a.pointsFor - a.pointsAgainst
          const bDiff = b.pointsFor - b.pointsAgainst
          if (bDiff !== aDiff) return bDiff - aDiff
          return b.pointsFor - a.pointsFor
        })
      }

      // For knockout stages, derive advancing count if not explicitly set
      let finalAdvancingCount = advancingCount
      if (stageFormat === 'knockout' && !advancingCount) {
        // In knockout, winners advance (top 50% of teams)
        const totalTeams = standings.length
        finalAdvancingCount = Math.ceil(totalTeams / 2)
      }

      // Group by bracket ONLY if not final stage
      // For final stage, keep teams in ranked order
      const brackets = new Map()
      if (isFinalStage) {
        // Don't group by bracket for final stage - keep ranked order
        brackets.set(1, standings)
      } else {
        // Group by bracket for non-final stages
        standings.forEach(team => {
          const bracketNum = team.bracketNumber || 1
          if (!brackets.has(bracketNum)) {
            brackets.set(bracketNum, [])
          }
          brackets.get(bracketNum).push(team)
        })
      }

      // Calculate which teams are actually advancing based on overall ranking across all brackets
      // This matches the tournament management logic
      const advancingTeamIds = new Set()
      if (!isFinalStage && finalAdvancingCount) {
        // Sort ALL teams across all brackets by overall ranking
        const allTeamsSorted = [...standings].sort((a, b) => {
          if (b.wins !== a.wins) return b.wins - a.wins
          const aDiff = a.pointsFor - a.pointsAgainst
          const bDiff = b.pointsFor - b.pointsAgainst
          if (bDiff !== aDiff) return bDiff - aDiff
          return b.pointsFor - a.pointsFor
        })
        
        // Take the top N teams across all brackets
        const advancingTeams = allTeamsSorted.slice(0, Math.min(finalAdvancingCount, allTeamsSorted.length))
        advancingTeams.forEach(team => advancingTeamIds.add(team.teamId))
      }

      return {
        stageName: stage.name,
        isComplete: isStageComplete(stage),
        brackets: Array.from(brackets.entries()).sort((a, b) => a[0] - b[0]),
        advancingCount: finalAdvancingCount,
        advancingTeamIds,
        stageFormat,
        isFinalStage
      }
    })
  }, [stages, tournament, category, exportedAt, dataVersion, refreshCounter])

  // Sort matches by time
  const sortedMatches = useMemo(() => {
    return [...category.matches].sort((a, b) => {
      if (!a.scheduledTime) return 1
      if (!b.scheduledTime) return -1
      return new Date(a.scheduledTime) - new Date(b.scheduledTime)
    })
  }, [category.matches, exportedAt, dataVersion, refreshCounter])

  const formatTime = (dateString) => {
    if (!dateString) return 'TBD'
    try {
      return new Date(dateString).toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit'
      })
    } catch {
      return 'TBD'
    }
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'TBD'
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric'
      })
    } catch {
      return 'TBD'
    }
  }

  return (
    <div className={`min-h-screen ${themeClasses.bg} ${themeClasses.text} pb-20 relative transition-colors duration-500`}>
      {/* Premium Animated Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className={`absolute top-1/4 left-1/4 w-96 h-96 ${isDarkMode ? 'bg-emerald-500/30' : 'bg-emerald-500/15'} rounded-full blur-3xl animate-pulse`}></div>
        <div className={`absolute bottom-1/4 right-1/4 w-96 h-96 ${isDarkMode ? 'bg-blue-500/30' : 'bg-blue-500/15'} rounded-full blur-3xl animate-pulse`} style={{ animationDelay: '1s' }}></div>
      </div>

      {/* Premium Header */}
      <div className={`${themeClasses.headerBg} sticky top-0 z-10 relative transition-all duration-500`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-5">
          <div className="flex items-center gap-3 sm:gap-5">
            {/* Back Button */}
            <button
              onClick={onBack}
              className={`flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 rounded-full transition-all duration-300 flex-shrink-0 ${
                isDarkMode
                  ? 'bg-white/[0.06] hover:bg-white/[0.12] text-white border border-white/[0.08]'
                  : 'bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-200'
              }`}
              title="Back to Home"
            >
              <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5" />
            </button>

            {/* Logo with Premium Styling */}
            {tournament.logoUrl ? (
              <div className="relative group">
                <div className={`absolute -inset-1 bg-gradient-to-r ${themeClasses.accent} rounded-xl blur opacity-25 group-hover:opacity-40 transition-opacity duration-300`}></div>
                <img
                  src={tournament.logoUrl}
                  alt="Tournament Logo"
                  className={`relative w-14 h-14 sm:w-24 sm:h-24 md:w-28 md:h-28 lg:w-32 lg:h-32 object-contain rounded-xl ${isDarkMode ? 'bg-white/5' : 'bg-white'} p-1.5 sm:p-2 border ${themeClasses.border} flex-shrink-0`}
                  onError={(e) => e.target.style.display = 'none'}
                />
              </div>
            ) : (
              <div className="relative group">
                <div className={`absolute -inset-1 bg-gradient-to-r ${themeClasses.accent} rounded-xl blur opacity-40 group-hover:opacity-60 transition-opacity duration-300`}></div>
                <div className={`relative w-14 h-14 sm:w-24 sm:h-24 md:w-28 md:h-28 lg:w-32 lg:h-32 bg-gradient-to-br ${themeClasses.accent} rounded-xl flex items-center justify-center shadow-lg flex-shrink-0`}>
                  <Trophy className="w-8 h-8 sm:w-12 sm:h-12 md:w-14 md:h-14 text-white" />
                </div>
              </div>
            )}

            <div className="flex-1 min-w-0">
              <h1 className={`text-sm sm:text-2xl md:text-3xl font-black leading-tight tracking-tight truncate ${themeClasses.text}`}>
                {tournament.name}
              </h1>
              <div className="hidden sm:flex mt-1 items-center gap-2">
                <svg className="w-4 h-4 text-emerald-500" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z"/>
                </svg>
                <span className={`text-sm font-semibold ${themeClasses.textMuted}`}>
                  Powered by Sports Control Hub
                </span>
              </div>
              <h2 className={`mt-0.5 sm:mt-2 text-sm sm:text-xl font-black ${colorTheme.text.primary} truncate`}>
                {category.title}
                {(category.skillLevel || category.ageGroup) && (
                  <span className={`ml-2 text-xs sm:text-base font-semibold ${themeClasses.textMuted}`}>
                    ({[category.skillLevel, category.ageGroup].filter(Boolean).join(' • ')})
                  </span>
                )}
              </h2>
            </div>

            <div className="flex flex-col items-end gap-1.5 sm:gap-2 flex-shrink-0">
              {exportedAt && (
                <div className="text-right hidden sm:block">
                  <p className={`text-xs font-semibold ${themeClasses.textMuted}`}>Data as of</p>
                  <p className={`text-sm ${colorTheme.text.primary} font-bold`}>{formatExportedAt(exportedAt)}</p>
                </div>
              )}
              <button
                onClick={handleManualRefresh}
                disabled={isRefreshing}
                className={`flex items-center gap-2 px-2.5 py-1.5 sm:px-3 sm:py-2 rounded-lg font-bold text-sm transition-all duration-300 shadow-md hover:shadow-lg ${
                  isDarkMode
                    ? 'bg-white/10 hover:bg-white/20 text-white border border-white/20'
                    : `${colorTheme.primaryShades[600]} hover:${colorTheme.primaryShades[700]} text-white`
                } disabled:opacity-50`}
                title="Refresh data"
              >
                <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
                <span className="hidden sm:inline">Refresh</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Content Area */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <div className={`${themeClasses.glass} rounded-2xl sm:rounded-3xl overflow-hidden transition-all duration-500`}>
          {/* Premium Tab Navigation */}
          <div className={`flex overflow-x-auto border-b ${themeClasses.border} ${isDarkMode ? 'bg-black/20' : 'bg-gray-50/50'}`}>
            {[
              { id: 'matches', label: 'Matches', icon: Trophy },
              { id: 'standings', label: 'Standings', icon: TrendingUp }
            ].map((tab) => {
              const Icon = tab.icon
              const isActiveTab = activeTab === tab.id
              return (
                <button
                  key={tab.id}
                  onClick={() => {
                    setActiveTab(tab.id)
                    trackCategoryDetailActivity('Tab Switch', { label: tab.label, category_name: category.title })
                  }}
                  className={`relative flex items-center gap-2.5 px-5 sm:px-6 py-4 font-semibold transition-all duration-300 whitespace-nowrap group ${
                    isActiveTab
                      ? `${themeClasses.text}`
                      : `${themeClasses.textMuted} ${themeClasses.hoverBg}`
                  }`}
                >
                  <Icon className={`w-5 h-5 transition-transform duration-300 ${isActiveTab ? 'scale-110' : 'group-hover:scale-105'}`} />
                  <span>{tab.label}</span>
                  {isActiveTab && (
                    <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-emerald-500 to-teal-400"></div>
                  )}
                </button>
              )
            })}
          </div>

          {/* Tab Content */}
          <div className="p-5 sm:p-6 lg:p-8">
        {activeTab === 'standings' && (
          <div className="space-y-6 animate-fade-in">
            {standingsByStage.map((stageData) => (
              <div key={`${stageData.stageName}-${exportedAt}`} id={`standings-${stageData.stageName.replace(/\s+/g, '-')}`} className={`${isDarkMode ? 'bg-white/[0.06] border-white/[0.1]' : 'bg-white border-gray-200'} rounded-2xl shadow-xl overflow-hidden border`}>
                {/* Stage Header */}
                <div className={`bg-gradient-to-r ${colorTheme.gradient} text-white p-4`}>
                  <div className="flex items-center justify-between">
                    <h2 className="text-xl font-black">
                      {stageData.stageName}
                    </h2>
                    <div className="flex items-center gap-2">
                      {!stageData.isComplete && (
                        <div className="flex items-center gap-2 bg-white bg-opacity-20 px-3 py-1 rounded-full">
                          <AlertCircle className="w-4 h-4" />
                          <span className="text-xs font-bold">In Progress</span>
                        </div>
                      )}
                      {stageData.isComplete && (
                        <div className="flex items-center gap-2 bg-white bg-opacity-20 px-3 py-1 rounded-full">
                          <CheckCircle className="w-4 h-4" />
                          <span className="text-xs font-bold">Complete</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Show standings only if stage is complete */}
                {stageData.isComplete ? (
                  <div className={`p-4 ${isDarkMode ? 'bg-white/[0.02]' : 'bg-gray-50'}`}>
                    {/* Simplified Standings Table */}
                    {stageData.brackets.map(([bracketNum, teams]) => (
                      <div key={bracketNum} className={`${isDarkMode ? 'bg-white/[0.06] border-white/[0.1]' : 'bg-white border-gray-200'} rounded-xl shadow-md overflow-hidden mb-4 last:mb-0 border`}>
                        {stageData.brackets.length > 1 && (
                          <div className={`bg-gradient-to-r ${colorTheme.gradient} text-white p-3`}>
                            <h3 className="text-md font-black">Bracket {bracketNum}</h3>
                          </div>
                        )}
                      
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead className={`${isDarkMode ? 'bg-white/[0.04] border-b-2 border-white/10' : 'bg-gray-50 border-b-2 border-gray-200'}`}>
                            <tr>
                              <th className={`px-1 sm:px-2 py-2 text-left text-[10px] sm:text-xs font-bold uppercase ${themeClasses.textMuted}`}>Rank</th>
                              <th className={`px-1 sm:px-2 py-2 text-left text-[10px] sm:text-xs font-bold uppercase ${themeClasses.textMuted}`}>Team</th>
                              <th className={`px-1 sm:px-2 py-2 text-center text-[10px] sm:text-xs font-bold uppercase ${themeClasses.textMuted}`}>W-L</th>
                              <th className={`px-1 sm:px-2 py-2 text-center text-[10px] sm:text-xs font-bold uppercase ${themeClasses.textMuted}`}>PF</th>
                              <th className={`px-1 sm:px-2 py-2 text-center text-[10px] sm:text-xs font-bold uppercase ${themeClasses.textMuted}`}>PA</th>
                              <th className={`px-1 sm:px-2 py-2 text-center text-[10px] sm:text-xs font-bold uppercase hidden sm:table-cell ${themeClasses.textMuted}`}>Diff</th>
                              <th className={`px-1 sm:px-2 py-2 text-center text-[10px] sm:text-xs font-bold uppercase ${themeClasses.textMuted}`}>Status</th>
                            </tr>
                          </thead>
                          <tbody className={`divide-y ${isDarkMode ? 'divide-white/[0.06]' : 'divide-gray-200'}`}>
                            {teams.map((team, index) => {
                              const isAdvancing = stageData.advancingTeamIds && stageData.advancingTeamIds.has(team.teamId)
                              const isEliminated = !stageData.isFinalStage && stageData.advancingTeamIds && !stageData.advancingTeamIds.has(team.teamId)
                              
                              return (
                                <tr 
                                  key={team.teamId} 
                                  onClick={() => {
                                    setSelectedTeam(team)
                                    setSelectedStage(stageData.stageName)
                                    trackCategoryDetailActivity('Team Selected', { label: `${getPlayerName(team.player1Name)} / ${getPlayerName(team.player2Name)}`, stage_name: stageData.stageName, category_name: category.title })
                                  }}
                                  className={`transition-colors cursor-pointer ${
                                    isAdvancing ? (isDarkMode ? 'bg-emerald-500/10 hover:bg-emerald-500/15' : `${colorTheme.primaryShades[50]} hover:${colorTheme.primaryShades[100]}`) :
                                    isEliminated ? (isDarkMode ? 'bg-red-500/10 hover:bg-red-500/15' : `${colorTheme.secondaryShades[50]} hover:${colorTheme.secondaryShades[100]}`) :
                                    isDarkMode ? 'hover:bg-white/[0.04]' : 'hover:bg-gray-50'
                                  }`}>
                                  {/* Rank */}
                                  <td className="px-1 sm:px-2 py-2">
                                    <div className={`w-7 h-7 sm:w-10 sm:h-10 rounded-full flex items-center justify-center font-black text-xs sm:text-lg ${
                                      index === 0 ? 'bg-gradient-to-br from-yellow-400 to-yellow-600 text-white' :
                                      index === 1 ? 'bg-gradient-to-br from-gray-300 to-gray-500 text-white' :
                                      index === 2 ? 'bg-gradient-to-br from-orange-400 to-orange-600 text-white' :
                                      isDarkMode ? 'bg-white/[0.08] text-white/50' : 'bg-gray-100 text-gray-600'
                                    }`}>
                                      {index + 1}
                                    </div>
                                  </td>
                                  
                                  {/* Team Info */}
                                  <td className="px-1 sm:px-2 py-2">
                                    <h3 className={`font-bold text-xs sm:text-base leading-tight ${
                                      isAdvancing ? `${colorTheme.text.primary}` : themeClasses.text
                                    }`}>
                                      {getPlayerName(team.player1Name)} / {getPlayerName(team.player2Name)}
                                    </h3>
                                  </td>
                                  
                                  {/* W-L */}
                                  <td className={`px-1 sm:px-2 py-2 text-center font-black text-xs sm:text-sm ${themeClasses.text}`}>
                                    {team.wins}-{team.losses}
                                  </td>
                                  
                                  {/* PF */}
                                  <td className={`px-1 sm:px-2 py-2 text-center font-black text-xs sm:text-sm ${themeClasses.text}`}>
                                    {team.pointsFor}
                                  </td>
                                  
                                  {/* PA */}
                                  <td className={`px-1 sm:px-2 py-2 text-center font-black text-xs sm:text-sm ${themeClasses.text}`}>
                                    {team.pointsAgainst}
                                  </td>
                                  
                                  {/* Diff */}
                                  <td className="px-1 sm:px-2 py-2 text-center hidden sm:table-cell">
                                    <span className={`font-black text-xs sm:text-sm ${
                                      team.pointsFor - team.pointsAgainst > 0 ? (isDarkMode ? 'text-green-400' : 'text-green-600') :
                                      team.pointsFor - team.pointsAgainst < 0 ? (isDarkMode ? 'text-red-400' : 'text-red-600') :
                                      themeClasses.text
                                    }`}>
                                      {team.pointsFor - team.pointsAgainst > 0 ? '+' : ''}
                                      {team.pointsFor - team.pointsAgainst}
                                    </span>
                                  </td>
                                  
                                  {/* Status */}
                                  <td className="px-1 sm:px-2 py-2 text-center">
                                    {stageData.isFinalStage ? (
                                      // Final stage: show placement
                                      <span className={`inline-flex items-center gap-0.5 sm:gap-1 text-[10px] sm:text-xs px-1.5 sm:px-3 py-0.5 sm:py-1 rounded-full font-bold whitespace-nowrap ${
                                        index === 0 ? 'bg-gradient-to-r from-yellow-400 to-yellow-600 text-white' :
                                        index === 1 ? 'bg-gradient-to-r from-gray-300 to-gray-500 text-white' :
                                        index === 2 ? 'bg-gradient-to-r from-orange-400 to-orange-600 text-white' :
                                        isDarkMode ? 'bg-white/[0.08] text-white/50' : 'bg-gray-200 text-gray-700'
                                      }`}>
                                        <Trophy className="w-2 h-2 sm:w-3 sm:h-3" />
                                        <span className="hidden sm:inline">
                                          {index === 0 ? '1st Place' :
                                           index === 1 ? '2nd Place' :
                                           index === 2 ? '3rd Place' :
                                           `${index + 1}th Place`}
                                        </span>
                                        <span className="sm:hidden">
                                          {index === 0 ? '1st' :
                                           index === 1 ? '2nd' :
                                           index === 2 ? '3rd' :
                                           `${index + 1}th`}
                                        </span>
                                      </span>
                                    ) : (
                                      // Non-final stage: show advancing/eliminated
                                      <>
                                        {isAdvancing && (
                                          <span className={`inline-flex items-center gap-0.5 sm:gap-1 text-[10px] sm:text-xs ${colorTheme.primaryShades[600]} text-white px-1.5 sm:px-3 py-0.5 sm:py-1 rounded-full font-bold whitespace-nowrap`}>
                                            <CheckCircle className="w-2 h-2 sm:w-3 sm:h-3" />
                                            <span className="hidden sm:inline">Advancing</span>
                                            <span className="sm:hidden">Adv</span>
                                          </span>
                                        )}
                                        {isEliminated && (
                                          <span className="inline-flex items-center gap-0.5 sm:gap-1 text-[10px] sm:text-xs bg-red-600 text-white px-1.5 sm:px-3 py-0.5 sm:py-1 rounded-full font-bold whitespace-nowrap">
                                            <X className="w-2 h-2 sm:w-3 sm:h-3" />
                                            <span className="hidden sm:inline">Eliminated</span>
                                            <span className="sm:hidden">Elim</span>
                                          </span>
                                        )}
                                        {!isAdvancing && !isEliminated && (
                                          <span className={`text-[10px] sm:text-xs font-semibold ${themeClasses.textMuted}`}>—</span>
                                        )}
                                      </>
                                    )}
                                  </td>
                                </tr>
                              )
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ))}
                  
                  {/* Overall Summary for Round Robin with multiple brackets */}
                  {stageData.stageFormat === 'round_robin' && stageData.brackets.length > 1 && (
                    <div className={`${isDarkMode ? 'bg-white/[0.06] border-white/[0.1]' : 'bg-white border-gray-200'} rounded-xl shadow-md overflow-hidden border`}>
                      <div className={`bg-gradient-to-r ${colorTheme.gradient} text-white p-4`}>
                        <h3 className="text-lg font-black flex items-center gap-2">
                          <Trophy className="w-5 h-5" />
                          {stageData.stageName} - Overall Rankings
                        </h3>
                        <p className="text-sm opacity-80 mt-1">Combined standings across all brackets</p>
                      </div>
                      
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead className={`${isDarkMode ? 'bg-white/[0.04] border-b-2 border-white/10' : 'bg-gray-50 border-b-2 border-gray-200'}`}>
                            <tr>
                              <th className={`px-1 sm:px-2 py-2 text-left text-[10px] sm:text-xs font-bold uppercase ${themeClasses.textMuted}`}>Rank</th>
                              <th className={`px-1 sm:px-2 py-2 text-left text-[10px] sm:text-xs font-bold uppercase ${themeClasses.textMuted}`}>Team</th>
                              <th className={`px-1 sm:px-2 py-2 text-center text-[10px] sm:text-xs font-bold uppercase ${themeClasses.textMuted}`}>Bracket</th>
                              <th className={`px-1 sm:px-2 py-2 text-center text-[10px] sm:text-xs font-bold uppercase ${themeClasses.textMuted}`}>W-L</th>
                              <th className={`px-1 sm:px-2 py-2 text-center text-[10px] sm:text-xs font-bold uppercase ${themeClasses.textMuted}`}>PF</th>
                              <th className={`px-1 sm:px-2 py-2 text-center text-[10px] sm:text-xs font-bold uppercase ${themeClasses.textMuted}`}>PA</th>
                              <th className={`px-1 sm:px-2 py-2 text-center text-[10px] sm:text-xs font-bold uppercase hidden sm:table-cell ${themeClasses.textMuted}`}>Diff</th>
                            </tr>
                          </thead>
                          <tbody className={`divide-y ${isDarkMode ? 'divide-white/[0.06]' : 'divide-gray-200'}`}>
                            {stageData.brackets
                              .flatMap(([bracketNum, teams]) => teams.map(team => ({ ...team, bracketNum })))
                              .sort((a, b) => {
                                if (b.wins !== a.wins) return b.wins - a.wins
                                const aDiff = a.pointsFor - a.pointsAgainst
                                const bDiff = b.pointsFor - b.pointsAgainst
                                if (bDiff !== aDiff) return bDiff - aDiff
                                return b.pointsFor - a.pointsFor
                              })
                              .map((team, index) => (
                                <tr 
                                  key={team.teamId} 
                                  onClick={() => {
                                    setSelectedTeam(team)
                                    setSelectedStage(stageData.stageName)
                                  }}
                                  className={`cursor-pointer ${isDarkMode ? 'hover:bg-white/[0.04]' : 'hover:bg-gray-50'}`}
                                >
                                  <td className="px-1 sm:px-2 py-2">
                                    <div className={`w-7 h-7 sm:w-10 sm:h-10 rounded-full flex items-center justify-center font-black text-xs sm:text-lg ${
                                      index === 0 ? 'bg-gradient-to-br from-yellow-400 to-yellow-600 text-white' :
                                      index === 1 ? 'bg-gradient-to-br from-gray-300 to-gray-500 text-white' :
                                      index === 2 ? 'bg-gradient-to-br from-orange-400 to-orange-600 text-white' :
                                      isDarkMode ? 'bg-white/[0.08] text-white/50' : 'bg-gray-100 text-gray-600'
                                    }`}>
                                      {index + 1}
                                    </div>
                                  </td>
                                  <td className="px-1 sm:px-2 py-2">
                                    <h3 className={`font-bold text-xs sm:text-base leading-tight ${themeClasses.text}`}>
                                      {getPlayerName(team.player1Name)} / {getPlayerName(team.player2Name)}
                                    </h3>
                                  </td>
                                  <td className="px-1 sm:px-2 py-2 text-center">
                                    <span className={`inline-block px-1.5 sm:px-2 py-0.5 sm:py-1 rounded text-[10px] sm:text-xs font-bold whitespace-nowrap ${isDarkMode ? 'bg-indigo-500/20 text-indigo-300' : 'bg-indigo-100 text-indigo-700'}`}>
                                      <span className="hidden sm:inline">Bracket </span>
                                      {team.bracketNum}
                                    </span>
                                  </td>
                                  <td className={`px-1 sm:px-2 py-2 text-center font-black text-xs sm:text-sm ${themeClasses.text}`}>
                                    {team.wins}-{team.losses}
                                  </td>
                                  <td className={`px-1 sm:px-2 py-2 text-center font-black text-xs sm:text-sm ${themeClasses.text}`}>
                                    {team.pointsFor}
                                  </td>
                                  <td className={`px-1 sm:px-2 py-2 text-center font-black text-xs sm:text-sm ${themeClasses.text}`}>
                                    {team.pointsAgainst}
                                  </td>
                                  <td className="px-1 sm:px-2 py-2 text-center hidden sm:table-cell">
                                    <span className={`font-black text-xs sm:text-sm ${
                                      team.pointsFor - team.pointsAgainst > 0 ? (isDarkMode ? 'text-green-400' : 'text-green-600') :
                                      team.pointsFor - team.pointsAgainst < 0 ? (isDarkMode ? 'text-red-400' : 'text-red-600') :
                                      themeClasses.text
                                    }`}>
                                      {team.pointsFor - team.pointsAgainst > 0 ? '+' : ''}
                                      {team.pointsFor - team.pointsAgainst}
                                    </span>
                                  </td>
                                </tr>
                              ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                  </div>
                ) : (
                  <div className={`p-8 text-center ${isDarkMode ? 'bg-white/[0.02]' : ''}`}>
                    <AlertCircle className="w-12 h-12 text-amber-500 mx-auto mb-3" />
                    <p className={`font-semibold ${themeClasses.textMuted}`}>
                      Standings will be available when all matches in this stage are completed
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {activeTab === 'matches' && (
          <div className="space-y-6 animate-fade-in">
            {stages.map((stage) => {
              const realMatches = stage.matches.filter(m => m.team1Id && m.team2Id)
              const hasStarted = realMatches.some(m => m.status === 'completed' || m.status === 'in_progress')
              return (
              <div key={`${stage.name}-${exportedAt}`} className={`${isDarkMode ? 'bg-white/[0.06] border-white/[0.1]' : 'bg-white border-gray-200'} rounded-2xl shadow-xl overflow-hidden border ${!hasStarted ? 'opacity-70' : ''}`}>
                {/* Stage Header */}
                <div className={`${hasStarted ? `bg-gradient-to-r ${colorTheme.gradient}` : 'bg-gradient-to-r from-gray-400 to-gray-500'} text-white p-4`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <h2 className="text-xl font-black">{stage.name}</h2>
                      <p className="text-sm opacity-80 mt-1">
                        {hasStarted
                          ? `${realMatches.filter(m => m.status === 'completed').length} / ${realMatches.length} matches completed`
                          : 'Not yet started'
                        }
                      </p>
                    </div>
                    {!hasStarted && (
                      <span className="bg-white/20 text-white text-xs font-bold px-3 py-1 rounded-full">
                        UPCOMING
                      </span>
                    )}
                  </div>
                </div>

                {/* Matches in this stage */}
                <div className={`p-4 space-y-4 ${isDarkMode ? 'bg-white/[0.02]' : 'bg-gray-50'}`}>
                  {stage.matches
                    .filter(m => m.team1Id && m.team2Id)
                    .sort((a, b) => {
                      if (!a.scheduledTime) return 1
                      if (!b.scheduledTime) return -1
                      return new Date(a.scheduledTime) - new Date(b.scheduledTime)
                    })
                    .map((match) => (
              <div key={match.id} id={`match-${match.id}`} className={`${isDarkMode ? 'bg-white/[0.06] border-white/[0.1]' : 'bg-white border-gray-200'} rounded-xl shadow-md overflow-hidden animate-content-update border`}>
                {/* Match Header */}
                <div className={`p-4 ${
                  match.status === 'completed' ? `bg-gradient-to-r ${colorTheme.gradient}` :
                  match.status === 'in_progress' ? `${colorTheme.primaryShades[500]}` :
                  'bg-gradient-to-r from-gray-400 to-gray-600'
                }`}>
                  <div className="flex items-center justify-between text-white">
                    <div className="flex items-center gap-2">
                      {match.status === 'completed' ? (
                        <CheckCircle className="w-5 h-5" />
                      ) : match.status === 'in_progress' ? (
                        <Play className="w-5 h-5" />
                      ) : (
                        <Clock className="w-5 h-5" />
                      )}
                      <span className="font-bold text-sm">
                        {match.status === 'completed' ? 'COMPLETED' :
                         match.status === 'in_progress' ? 'IN PROGRESS' :
                         'SCHEDULED'}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 text-sm">
                      {match.courtNumber && (
                        <div className="flex items-center gap-1">
                          <MapPin className="w-4 h-4" />
                          Court {match.courtNumber}
                        </div>
                      )}
                      {match.scheduledTime && (
                        <div className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {formatDate(match.scheduledTime)} {formatTime(match.scheduledTime)}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Teams */}
                <div className="p-6">
                  {/* Team 1 */}
                  <div className={`flex items-center justify-between mb-4 pb-4 border-b-2 ${
                    match.winnerId === match.team1Id ? `${colorTheme.border.primary}` : isDarkMode ? 'border-white/10' : 'border-gray-200'
                  }`}>
                    <div className="flex-1 min-w-0 mr-3">
                      <h3 className={`font-bold text-sm sm:text-lg truncate ${
                        match.winnerId === match.team1Id ? `${colorTheme.text.primary}` : themeClasses.text
                      }`}>
                        {getPlayerName(match.team1Player1)} / {getPlayerName(match.team1Player2)}
                      </h3>
                    </div>
                    {match.status !== 'scheduled' && (
                      <div className={`text-2xl sm:text-3xl font-black flex-shrink-0 ${
                        match.winnerId === match.team1Id ? `${colorTheme.text.primary}` : isDarkMode ? 'text-white/30' : 'text-gray-400'
                      }`}>
                        {match.team1Score || 0}
                      </div>
                    )}
                  </div>

                  {/* Team 2 */}
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0 mr-3">
                      <h3 className={`font-bold text-sm sm:text-lg truncate ${
                        match.winnerId === match.team2Id ? `${colorTheme.text.primary}` : themeClasses.text
                      }`}>
                        {getPlayerName(match.team2Player1)} / {getPlayerName(match.team2Player2)}
                      </h3>
                    </div>
                    {match.status !== 'scheduled' && (
                      <div className={`text-2xl sm:text-3xl font-black flex-shrink-0 ${
                        match.winnerId === match.team2Id ? `${colorTheme.text.primary}` : isDarkMode ? 'text-white/30' : 'text-gray-400'
                      }`}>
                        {match.team2Score || 0}
                      </div>
                    )}
                  </div>

                  {/* Game Scores */}
                  {match.games && match.games.length > 0 && (
                    <div className={`mt-4 pt-4 border-t ${isDarkMode ? 'border-white/10' : 'border-gray-200'}`}>
                      <p className={`text-xs font-semibold mb-2 ${themeClasses.textMuted}`}>GAME SCORES</p>
                      <div className="flex flex-wrap gap-2">
                        {match.games.map((game, index) => (
                          <div key={index} className={`${isDarkMode ? 'bg-white/[0.06]' : 'bg-gray-100'} rounded-lg px-3 py-2 text-center`}>
                            <p className={`text-xs font-semibold ${themeClasses.textMuted}`}>Game {index + 1}</p>
                            <p className={`text-sm font-black ${themeClasses.text}`}>
                              {game.team1Score}-{game.team2Score}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Share Button — completed matches only */}
                  {match.status === 'completed' && match.winnerId && (
                    <div className={`mt-4 pt-4 border-t ${isDarkMode ? 'border-white/10' : 'border-gray-200'}`}>
                      <button
                        onClick={() => match.matchCardUrl ? setShareModalMatch(match) : handleGenerateAndShare(match)}
                        disabled={!!sharingMatchId}
                        className={`w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold transition-all duration-200 ${
                          isDarkMode
                            ? 'bg-white/[0.06] hover:bg-white/[0.12] text-white/80 hover:text-white'
                            : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                        } ${sharingMatchId === match.id ? 'opacity-70' : ''}`}
                      >
                        {sharingMatchId === match.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Share2 className="w-4 h-4" />
                        )}
                        Share Result
                      </button>
                    </div>
                  )}
                </div>
              </div>
                    ))}
                </div>
              </div>
              )
            })}

            {category.matches.length === 0 && (
              <div className={`${isDarkMode ? 'bg-white/[0.06]' : 'bg-white'} rounded-2xl shadow-xl p-12 text-center`}>
                <Trophy className={`w-16 h-16 mx-auto mb-4 ${themeClasses.textMuted}`} />
                <p className={`font-semibold ${themeClasses.textMuted}`}>No matches scheduled yet</p>
              </div>
            )}
          </div>
        )}
          </div>
        </div>
      </div>

      {/* Team Match Details Modal */}
      {selectedTeam && selectedStage && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" onClick={() => {
          setSelectedTeam(null)
          setSelectedStage(null)
        }}>
          <div className={`${isDarkMode ? 'bg-gray-900 border border-white/10' : 'bg-white'} rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto`} onClick={(e) => e.stopPropagation()}>
            {/* Modal Header */}
            <div className={`bg-gradient-to-r ${colorTheme.gradient} text-white p-6 sticky top-0`}>
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-black">
                    {getPlayerName(selectedTeam.player1Name)} / {getPlayerName(selectedTeam.player2Name)}
                  </h2>
                  <p className="opacity-80 text-xs mt-2">{selectedStage}</p>
                </div>
                <button 
                  onClick={() => {
                    setSelectedTeam(null)
                    setSelectedStage(null)
                  }}
                  className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-2 transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              
              {/* Team Stats */}
              <div className="grid grid-cols-4 gap-4 mt-4">
                <div className="bg-white bg-opacity-20 rounded-lg p-3 text-center">
                  <p className="text-xs opacity-80">Wins</p>
                  <p className="text-2xl font-black">{selectedTeam.wins}</p>
                </div>
                <div className="bg-white bg-opacity-20 rounded-lg p-3 text-center">
                  <p className="text-xs opacity-80">Losses</p>
                  <p className="text-2xl font-black">{selectedTeam.losses}</p>
                </div>
                <div className="bg-white bg-opacity-20 rounded-lg p-3 text-center">
                  <p className="text-xs opacity-80">PF</p>
                  <p className="text-2xl font-black">{selectedTeam.pointsFor}</p>
                </div>
                <div className="bg-white bg-opacity-20 rounded-lg p-3 text-center">
                  <p className="text-xs opacity-80">PA</p>
                  <p className="text-2xl font-black">{selectedTeam.pointsAgainst}</p>
                </div>
              </div>
            </div>

            {/* Match List */}
            <div className="p-6">
              <h3 className={`text-lg font-bold mb-4 ${themeClasses.text}`}>Match History</h3>
              <div className="space-y-4">
                {category.matches
                  .filter(m => 
                    m.stageName === selectedStage && 
                    (m.team1Id === selectedTeam.teamId || m.team2Id === selectedTeam.teamId)
                  )
                  .map(match => {
                    const isTeam1 = match.team1Id === selectedTeam.teamId
                    const opponentPlayer1 = isTeam1 ? match.team2Player1 : match.team1Player1
                    const opponentPlayer2 = isTeam1 ? match.team2Player2 : match.team1Player2
                    const opponentName = `${getPlayerName(opponentPlayer1)} / ${getPlayerName(opponentPlayer2)}`
                    const teamScore = isTeam1 ? match.team1Score : match.team2Score
                    const opponentScore = isTeam1 ? match.team2Score : match.team1Score
                    const isWinner = match.winnerId === selectedTeam.teamId
                    
                    return (
                      <div key={match.id} className={`border-2 rounded-lg p-4 ${
                        match.status === 'completed' 
                          ? (isWinner 
                            ? (isDarkMode ? `${colorTheme.border.primary} bg-emerald-500/10` : `${colorTheme.border.primary} ${colorTheme.primaryShades[50]}`) 
                            : (isDarkMode ? `${colorTheme.border.secondary} bg-red-500/10` : `${colorTheme.border.secondary} ${colorTheme.secondaryShades[50]}`)) 
                          : isDarkMode ? 'border-white/10 bg-white/[0.04]' : 'border-gray-200 bg-gray-50'
                      }`}>
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            {match.status === 'completed' && (
                              <div className={`w-2 h-2 rounded-full ${isWinner ? colorTheme.primaryShades[600] : colorTheme.secondaryShades[600]}`}></div>
                            )}
                            <span className={`font-bold ${themeClasses.text}`}>vs {opponentName}</span>
                          </div>
                          {match.status === 'completed' && (
                            <div className="text-lg font-black">
                              <span className={isWinner ? `${colorTheme.text.primary}` : themeClasses.textMuted}>{teamScore}</span>
                              <span className={`mx-1 ${themeClasses.textMuted}`}>-</span>
                              <span className={!isWinner ? `${colorTheme.text.secondary}` : themeClasses.textMuted}>{opponentScore}</span>
                            </div>
                          )}
                        </div>
                        
                        {match.scheduledTime && (
                          <div className={`flex items-center gap-2 text-xs ${themeClasses.textMuted}`}>
                            <Clock className="w-3 h-3" />
                            {formatTime(match.scheduledTime)}
                          </div>
                        )}
                        
                        {match.games && match.games.length > 0 && (
                          <div className={`mt-3 pt-3 border-t ${isDarkMode ? 'border-white/10' : 'border-gray-200'}`}>
                            <p className={`text-xs font-semibold mb-2 ${themeClasses.textMuted}`}>Game Scores</p>
                            <div className="flex gap-2 flex-wrap">
                              {match.games.map((game, index) => {
                                const teamGameScore = isTeam1 ? game.team1Score : game.team2Score
                                const oppGameScore = isTeam1 ? game.team2Score : game.team1Score
                                const wonGame = teamGameScore > oppGameScore
                                
                                return (
                                  <div key={index} className={`rounded px-2 py-1 text-xs font-bold ${
                                    wonGame 
                                      ? (isDarkMode ? 'bg-emerald-500/20 text-emerald-300' : 'bg-emerald-100 text-emerald-700') 
                                      : (isDarkMode ? 'bg-red-500/20 text-red-300' : 'bg-rose-100 text-rose-700')
                                  }`}>
                                    Game {index + 1}: {teamGameScore}-{oppGameScore}
                                  </div>
                                )
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    )
                  })}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Server vs Client Image Choice Modal */}
      {shareModalMatch && (
        <div
          className="fixed inset-0 bg-black/60 flex items-end justify-center z-50 p-4"
          onClick={() => setShareModalMatch(null)}
        >
          <div
            className={`${isDarkMode ? 'bg-gray-900 border border-white/10' : 'bg-white'} rounded-2xl shadow-2xl w-full max-w-sm p-5`}
            onClick={e => e.stopPropagation()}
          >
            <h3 className={`text-base font-bold mb-1 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Share Match Result</h3>
            <p className={`text-xs mb-4 ${isDarkMode ? 'text-white/50' : 'text-gray-500'}`}>Choose which image to share</p>
            <div className="flex flex-col gap-2">
              <button
                onClick={() => { setShareModalMatch(null); handleShareServerImage(shareModalMatch); }}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold transition-all ${
                  isDarkMode
                    ? 'bg-white/[0.08] hover:bg-white/[0.14] text-white'
                    : 'bg-gray-100 hover:bg-gray-200 text-gray-800'
                }`}
              >
                <Share2 className="w-4 h-4 shrink-0" />
                <div className="text-left">
                  <div>Server Image</div>
                  <div className={`text-xs font-normal ${isDarkMode ? 'text-white/50' : 'text-gray-500'}`}>High-quality card with player photos</div>
                </div>
              </button>
              <button
                onClick={() => { setShareModalMatch(null); handleShareMatch(shareModalMatch.id); }}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold transition-all ${
                  isDarkMode
                    ? 'bg-white/[0.04] hover:bg-white/[0.10] text-white/70 hover:text-white'
                    : 'bg-gray-50 hover:bg-gray-100 text-gray-600'
                }`}
              >
                <Share2 className="w-4 h-4 shrink-0" />
                <div className="text-left">
                  <div>Generate New Image</div>
                  <div className={`text-xs font-normal ${isDarkMode ? 'text-white/50' : 'text-gray-500'}`}>Client-side generated image</div>
                </div>
              </button>
            </div>
            <button
              onClick={() => setShareModalMatch(null)}
              className={`mt-3 w-full py-2 rounded-xl text-sm transition-all ${
                isDarkMode ? 'text-white/40 hover:text-white/70' : 'text-gray-400 hover:text-gray-600'
              }`}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Share Toast */}
      {shareToast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 animate-in slide-in-from-bottom fade-in duration-300">
          <div className={`px-5 py-3 rounded-xl shadow-2xl font-semibold text-sm flex items-center gap-2 ${
            shareToast.type === 'error'
              ? 'bg-red-500 text-white'
              : 'bg-emerald-500 text-white'
          }`}>
            {shareToast.type === 'copied' && <CheckCircle className="w-4 h-4" />}
            {shareToast.type === 'shared' && <Share2 className="w-4 h-4" />}
            {shareToast.message}
          </div>
        </div>
      )}
    </div>
  )
}

export default CategoryView
